import React from 'react';
import { Progress } from '@/components/ui/progress';
import boostx2gems from '@/assets/boostx2gems.png';
import { getXpForLevel, getLevelRewards, type LevelReward } from '@/data/leveling';
import { Trophy, Gift } from 'lucide-react';
import { Gems } from './Gems';
import { UserAvatar } from './UserAvatar';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useState, useEffect } from 'react';

interface LevelProgressBarProps {
    xp: number;
    playerLevel: number;
    className?: string;
    small?: boolean;
}

export const LevelProgressBar = ({ xp, playerLevel, className = "", small = false }: LevelProgressBarProps) => {
    const nextLevel = playerLevel + 1;
    const currentLevelXp = getXpForLevel(playerLevel);
    const nextLevelXp = getXpForLevel(nextLevel);
    const relativeXp = xp - currentLevelXp;
    const xpDifference = nextLevelXp - currentLevelXp;
    const progress = (relativeXp / xpDifference) * 100;

    // Search for the next level that has a reward
    let nextRewardLevel = nextLevel;
    let nextReward: LevelReward | null = null;
    const { user } = useAuth();
    const [profile, setProfile] = useState<{ avatar_url?: string; display_name?: string } | null>(null);
    const [rewardBorderStyle, setRewardBorderStyle] = useState<string | undefined>(undefined);

    // Look ahead up to 3 levels for the next reward
    for (let l = nextLevel; l <= nextLevel + 2; l++) {
        const r = getLevelRewards(l);
        if (r) {
            nextRewardLevel = l;
            nextReward = r;
            break;
        }
    }

    useEffect(() => {
        let channel: any;

        const fetchData = async () => {
            if (!user) return;

            // Fetch profile
            const { data: profileData } = await supabase
                .from('profiles')
                .select('avatar_url, display_name')
                .eq('id', user.id)
                .single();

            if (profileData) setProfile(profileData);

            // If the next reward is a border, resolve the slug to its URL (or actual style)
            if (nextReward?.border_style) {
                const { data: itemData } = await supabase
                    .from('shop_items')
                    .select('metadata')
                    .eq('type', 'border')
                    .filter('metadata->>style_slug', 'eq', nextReward.border_style)
                    .maybeSingle();

                if (itemData?.metadata?.style) {
                    setRewardBorderStyle(itemData.metadata.style);
                } else {
                    // Fallback to the slug itself (maybe it's a hardcoded one like 'neon')
                    setRewardBorderStyle(nextReward.border_style);
                }
            } else {
                setRewardBorderStyle(undefined);
            }
        };

        const handleProfileUpdate = () => fetchData();

        fetchData();

        if (user?.id) {
            // Listen for local events
            window.addEventListener('profile-updated', handleProfileUpdate);

            channel = supabase
                .channel(`pb_profile_${user.id}`)
                .on(
                    'postgres_changes',
                    {
                        event: 'UPDATE',
                        schema: 'public',
                        table: 'profiles',
                        filter: `id=eq.${user.id}`,
                    },
                    () => {
                        handleProfileUpdate();
                    }
                )
                .subscribe();
        }

        return () => {
            window.removeEventListener('profile-updated', handleProfileUpdate);
            if (channel) {
                supabase.removeChannel(channel);
            }
        };
    }, [user?.id, nextReward?.border_style]);

    return (
        <div className={`relative flex items-center w-full bg-[#1A1C2E] rounded-xl overflow-hidden border border-white/5 shadow-2xl ${className}`}>
            {/* Level Indicator */}
            <div className={`flex  ${small ? 'flex-row gap-1 min-w-[50px] ' : 'flex-col min-w-[80px]'} items-center justify-center bg-[#6366F1] py-2 self-stretch`}>
                <span className={`text-[10px] uppercase tracking-widest text-[#E0E7FF] font-black opacity-80 ${small ? 'mb-0' : 'mb-0.5'}`}>Lvl</span>
                <span className={`${small ? 'text-sm' : 'text-xl'} font-black text-white leading-none`}>{playerLevel}</span>
            </div>

            {/* Progress Section */}
            <div className={`flex-1 flex ${small ? 'flex-row-reverse gap-2 items-center px-2' : 'flex-col px-6'} justify-center`}>
                <div className={`flex items-center justify-between ${small ? 'mb-0' : 'mb-2'}`}>
                    <div className="flex items-center gap-2">
                        <span className="text-[#94A3B8] text-[11px] font-bold font-mono whitespace-nowrap">
                            {xp.toLocaleString()} / {nextLevelXp.toLocaleString()} XP
                        </span>
                    </div>
                    {/* {nextReward && (
                        <span className="text-[10px] text-[#818CF8] font-bold uppercase tracking-wider">
                            to Lvl. {nextRewardLevel}
                        </span>
                    )} */}
                </div>

                <div className="min-w-[80px] relative h-3 w-full bg-[#0F111A] rounded-full overflow-hidden border border-white/5">
                    <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#6366F1] via-[#818CF8] to-[#6366F1] bg-[length:200%_100%] animate-pulse-slow transition-all duration-1000 ease-out shadow-[0_0_15px_rgba(99,102,241,0.5)]"
                        style={{ width: `${Math.min(100, progress)}%` }}
                    />
                </div>
            </div>

            {/* Reward Indicator */}
            {!small && (
                <div className="px-6 flex items-center justify-center border-l border-white/5 bg-[#24273F]/50 min-w-[90px] self-stretch group transition-colors hover:bg-[#24273F]">
                    {nextReward ? (
                        <div className="flex flex-col items-center gap-1.5 animate-bounce-subtle">
                            {nextReward.gems && (
                                <div className="relative">
                                    <div className="absolute inset-0 bg-white/20 blur-xl rounded-full" />
                                    <span className="text-[16px] text-[#818CF8] font-bold uppercase tracking-wider">{nextReward.gems} </span><Gems size={16} />
                                </div>
                            )}
                            {nextReward.boost_days && (
                                <div className="flex flex-col items-center justify-center text-[8px] font-bold mt-[-4px]">
                                    <img src={boostx2gems} alt="boostx2gems" className="w-8 h-8" />
                                    {nextReward.boost_days} DAY
                                </div>
                            )}
                            {nextReward.border_style && !nextReward.gems && !nextReward.boost_days && (
                                <UserAvatar
                                    size="md"
                                    src={profile?.avatar_url}
                                    fallback={profile?.display_name?.charAt(0) || "U"}
                                    borderStyle={rewardBorderStyle}
                                />
                            )}
                        </div>
                    ) : (
                        <Gift className="w-6 h-6 text-[#94A3B8]/30" />
                    )}
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 3s ease-in-out infinite;
        }
        @keyframes pulse-slow {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 8s linear infinite;
        }
      `}} />
        </div>
    );
};
